import { getRUM } from '@codametrix/client-observability';
import { loggerFactory } from '@codametrix/ui-common';

const logger = loggerFactory('SsoLogin');

export const SSO_RUM_ACTION = 'sso_login';

const REDACTED_KEYS = new Set([
  'accesstoken',
  'access_token',
  'authorization',
  'authorization-cognito',
  'code',
  'id_token',
  'idtoken',
  'jwt',
  'password',
  'token'
]);

export type SsoLoginSeverity = 'warn' | 'error';

const truncate = (value: string | null | undefined, max = 200): string | undefined => {
  if (!value) {
    return undefined;
  }
  return value.length > max ? `${value.slice(0, max)}…` : value;
};

const sanitizeContext = (
  context: Record<string, unknown>
): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  Object.entries(context).forEach(([key, value]) => {
    if (value === undefined) {
      return;
    }
    if (REDACTED_KEYS.has(key.toLowerCase())) {
      out[key] =
        typeof value === 'string'
          ? { present: value.length > 0, length: value.length }
          : '[redacted]';
      return;
    }
    out[key] = value;
  });
  return out;
};

const paramKeys = (params: URLSearchParams): string[] => Array.from(params.keys());

const navigationType = (): string | undefined => {
  const entries = performance.getEntriesByType?.('navigation') as
    | PerformanceNavigationTiming[]
    | undefined;
  return entries?.[0]?.type;
};

/**
 * Hash/query shape of the SSO callback URL. Values that can contain JWTs or
 * auth codes are omitted; only keys and Cognito error fields are included.
 */
export const describeSsoCallbackLocation = (): Record<string, unknown> => {
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const search = window.location.search.startsWith('?')
    ? window.location.search.slice(1)
    : window.location.search;
  const hashParams = new URLSearchParams(hash);
  const queryParams = new URLSearchParams(search);

  return {
    pathname: window.location.pathname,
    hashPresent: hash.length > 0,
    queryPresent: search.length > 0,
    hashKeys: paramKeys(hashParams),
    queryKeys: paramKeys(queryParams),
    hasIdToken: Boolean(hashParams.get('id_token') || queryParams.get('id_token')),
    hasAccessToken: Boolean(
      hashParams.get('access_token') || queryParams.get('access_token')
    ),
    hasAuthCode: Boolean(hashParams.get('code') || queryParams.get('code')),
    idTokenLength: (hashParams.get('id_token') || queryParams.get('id_token') || '')
      .length,
    cognitoError: hashParams.get('error') || queryParams.get('error') || undefined,
    cognitoErrorDescription: truncate(
      hashParams.get('error_description') || queryParams.get('error_description')
    ),
    navigationType: navigationType(),
    visibilityState: document.visibilityState
  };
};

export const describeTokenPresence = (
  token: string | null | undefined
): { present: boolean; length: number } => ({
  present: Boolean(token && token.length > 0),
  length: token?.length ?? 0
});

/**
 * Console (warn/error, visible at the production default log level) plus a
 * Datadog RUM action named `sso_login`. Failures also call `addError` so they
 * show up in Error Tracking. Never throws.
 */
export const logSsoLogin = (
  step: string,
  context: Record<string, unknown> = {},
  severity: SsoLoginSeverity = 'warn'
): void => {
  const payload = { step, ...sanitizeContext(context) };
  if (severity === 'error') {
    logger.error(step, payload);
  } else {
    logger.warn(step, payload);
  }

  try {
    const rum = getRUM();
    rum.addAction(SSO_RUM_ACTION, payload);
    if (severity === 'error') {
      rum.addError(new Error(`SSO login: ${step}`), payload);
    }
  } catch {
    // Telemetry must never break login.
  }
};
